import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Sparkles, Shield, Clock, Users, AlertCircle } from 'lucide-react';

interface CustomerInfo {
  companyName: string;
  contactName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  businessType: string;
  employeeCount: string;
}

interface Plan {
  id: string;
  name: string;
  price: number;
  features: string[];
  description: string;
  icon: React.ReactNode;
  popular?: boolean;
}

const Subscription: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [customerInfo, setCustomerInfo] = useState<CustomerInfo>({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    businessType: '',
    employeeCount: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const plans: Plan[] = [
    {
      id: 'basic',
      name: 'Remote Support',
      price: 199,
      description: 'Essential remote IT support for your business',
      icon: <Users className="w-8 h-8" />,
      features: [
        'Remote technical support',
        'Email & phone assistance',
        'Software troubleshooting',
        'Basic system monitoring',
        'Business hours coverage'
      ]
    },
    {
      id: 'professional',
      name: 'Network Plus',
      price: 249,
      description: 'Remote support with network infrastructure',
      icon: <Shield className="w-8 h-8" />,
      popular: true,
      features: [
        'Everything in Remote Support',
        'Router & switch management',
        'ISP coordination & support',
        'Network configuration',
        'Priority response times',
        'Network health monitoring'
      ]
    },
    {
      id: 'enterprise',
      name: 'Complete Connectivity',
      price: 299,
      description: 'Full network management with WiFi upgrade',
      icon: <Sparkles className="w-8 h-8" />,
      features: [
        'Everything in Network Plus',
        'WiFi infrastructure upgrade',
        'Dual connectivity management',
        'Wired & wireless optimization',
        'Advanced network security',
        'Proactive monitoring',
        'Priority 24/7 support'
      ]
    }
  ];

  const states = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];

  const businessTypes = ['Independent Insurance Agency', 'Insurance Brokerage', 'Captive Insurance Agency', 'Insurance Carrier Office', 'Other'];
  const employeeCounts = ['1-5', '6-15', '16-30', '31-50', '50+'];

  // Check for pre-selected plan from URL parameter
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const planParam = params.get('plan');
    if (planParam && ['basic', 'professional', 'enterprise'].includes(planParam)) {
      setSelectedPlan(planParam);
    }
  }, [location]);

  const validateStep1 = () => {
    if (!selectedPlan) {
      setErrors({ plan: 'Please select a plan' });
      return false;
    }
    setErrors({});
    return true;
  };

  const validateStep2 = () => {
    const newErrors: Record<string, string> = {};
    if (!customerInfo.companyName) newErrors.companyName = 'Required';
    if (!customerInfo.contactName) newErrors.contactName = 'Required';
    if (!customerInfo.email) newErrors.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) newErrors.email = 'Invalid email';
    if (!customerInfo.phone) newErrors.phone = 'Required';
    if (!customerInfo.address) newErrors.address = 'Required';
    if (!customerInfo.city) newErrors.city = 'Required';
    if (!customerInfo.state) newErrors.state = 'Required';
    if (!customerInfo.zipCode) newErrors.zipCode = 'Required';
    if (!customerInfo.businessType) newErrors.businessType = 'Required';
    if (!customerInfo.employeeCount) newErrors.employeeCount = 'Required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) setStep(2);
    else if (step === 2 && validateStep2()) setStep(3);
  };

  const handleProceedToContract = async () => {
    const selectedPlanData = plans.find(plan => plan.id === selectedPlan);
    if (selectedPlanData) {
      const subscriptionData = {
        plan: {
          id: selectedPlanData.id,
          name: selectedPlanData.name,
          price: selectedPlanData.price,
          description: selectedPlanData.description,
          features: selectedPlanData.features
        },
        customerInfo: customerInfo
      };
      
      // Store in session storage for the confirmation page
      sessionStorage.setItem('subscriptionData', JSON.stringify(subscriptionData));
      
      // Send to API to store in DynamoDB
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/subscription-requests`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planId: selectedPlanData.id,
            planName: selectedPlanData.name,
            monthlyPrice: selectedPlanData.price,
            companyName: customerInfo.companyName,
            contactName: customerInfo.contactName,
            email: customerInfo.email,
            phone: customerInfo.phone,
            address: customerInfo.address,
            city: customerInfo.city,
            state: customerInfo.state,
            zipCode: customerInfo.zipCode,
            businessType: customerInfo.businessType,
            employeeCount: customerInfo.employeeCount,
            status: 'pending',
            requestDate: new Date().toISOString()
          })
        });
        
        if (!response.ok) {
          console.error('Failed to save subscription request');
        }
      } catch (error) {
        console.error('Error saving subscription request:', error);
      }
    }
    navigate('/subscription-confirmation');
  };

  const updateCustomerInfo = (field: keyof CustomerInfo, value: string) => {
    setCustomerInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-gray-50 to-slate-100 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-7xl">
        {/* Header - Outside Card */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent mb-2">
            Grid Managed Services
          </h1>
          <p className="text-gray-600">Complete your subscription in 3 easy steps</p>
        </div>

        {/* Main Wizard Card */}
        <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Card Header */}
          <div className="bg-gradient-to-r from-gray-50 to-white border-b border-gray-200 px-8 py-6">
            <div className="flex items-center justify-between">
              <button 
                onClick={() => navigate('/')}
                className="btn btn-secondary btn-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </button>
              <div className="text-sm text-gray-500">
                Step {step} of 3
              </div>
            </div>
          </div>

          {/* Card Content */}
          <div className="p-8 md:p-12">
          
          {/* Step 1: Plan Selection */}
          {step === 1 && (
            <div className="fade-in-up">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Choose Your IT Support Plan
                </h2>
                <p className="text-gray-600">
                  Select the perfect IT support plan for your business. All plans include our core services with varying levels of support.
                </p>
              </div>

              <div className="pricing-grid">
                {plans.map((plan) => (
                  <div 
                    key={plan.id}
                    onClick={() => {
                      setSelectedPlan(plan.id);
                      setErrors({});
                    }}
                    className={`pricing-card ${plan.popular ? 'featured' : ''} ${selectedPlan === plan.id ? 'selected-plan' : ''}`}
                    style={{ cursor: 'pointer', position: 'relative' }}
                  >
                    {plan.popular && (
                      <div className="pricing-badge">Most Popular</div>
                    )}
                    
                    {/* Selection Indicator */}
                    {selectedPlan === plan.id && (
                      <div style={{ position: 'absolute', top: '1rem', right: '1rem' }}>
                        <div style={{ 
                          width: '28px', 
                          height: '28px', 
                          borderRadius: '50%', 
                          background: '#16a34a',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}>
                          <Check style={{ width: '20px', height: '20px', color: 'white', fontWeight: 'bold' }} />
                        </div>
                      </div>
                    )}
                    
                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    
                    <div className="pricing-price">
                      ${plan.price}
                      <span className="text-lg font-normal text-gray-600">/month</span>
                    </div>
                    
                    <ul className="pricing-features" style={{ color: selectedPlan === plan.id ? '#16a34a' : undefined }}>
                      {plan.features.map((feature, featureIndex) => (
                        <li key={featureIndex}>{feature}</li>
                      ))}
                    </ul>
                    
                    <button 
                      className={`btn w-full ${selectedPlan === plan.id ? 'btn-primary' : 'btn-outline'}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedPlan(plan.id);
                        setErrors({});
                      }}
                    >
                      {selectedPlan === plan.id ? '✓ Selected' : 'Select Plan'}
                    </button>
                  </div>
                ))}
              </div>

              {errors.plan && (
                <div className="text-center mt-6">
                  <div className="inline-flex items-center gap-2 bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm font-medium border border-red-200">
                    <AlertCircle className="w-4 h-4" />
                    {errors.plan}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: Company Information */}
          {step === 2 && (
            <div className="fade-in-up">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Tell Us About Your Business
                </h2>
                <p className="text-gray-600">
                  We'll customize your IT support experience based on your needs
                </p>
              </div>

              <div 
                className="max-w-4xl mx-auto"
                style={{
                  border: '3px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                  borderRadius: '16px',
                  padding: '3rem'
                }}
              >
                {/* Contact Information Card */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Users className="w-5 h-5 text-red-600" />
                    Contact Information
                  </h3>
                  <div className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">Company Name *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.companyName ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.companyName}
                          onChange={(e) => updateCustomerInfo('companyName', e.target.value)}
                          placeholder="Your Company LLC"
                        />
                        {errors.companyName && <p className="text-red-600 text-sm mt-1 font-medium">{errors.companyName}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">Contact Name *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.contactName ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.contactName}
                          onChange={(e) => updateCustomerInfo('contactName', e.target.value)}
                          placeholder="John Doe"
                        />
                        {errors.contactName && <p className="text-red-600 text-sm mt-1 font-medium">{errors.contactName}</p>}
                      </div>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">Email Address *</label>
                        <input
                          type="email"
                          className={`form-input ${errors.email ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.email}
                          onChange={(e) => updateCustomerInfo('email', e.target.value)}
                          placeholder="john@company.com"
                        />
                        {errors.email && <p className="text-red-600 text-sm mt-1 font-medium">{errors.email}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">Phone Number *</label>
                        <input
                          type="tel"
                          className={`form-input ${errors.phone ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.phone}
                          onChange={(e) => updateCustomerInfo('phone', e.target.value)}
                          placeholder="(555) 123-4567"
                        />
                        {errors.phone && <p className="text-red-600 text-sm mt-1 font-medium">{errors.phone}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Address Card */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-8 mb-6 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-red-600" />
                    Business Address
                  </h3>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-2">Street Address *</label>
                      <input
                        type="text"
                        className={`form-input ${errors.address ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                        value={customerInfo.address}
                        onChange={(e) => updateCustomerInfo('address', e.target.value)}
                        placeholder="123 Main Street"
                      />
                      {errors.address && <p className="text-red-600 text-sm mt-1 font-medium">{errors.address}</p>}
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="col-span-2">
                        <label className="block text-sm font-bold text-gray-800 mb-2">City *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.city ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.city}
                          onChange={(e) => updateCustomerInfo('city', e.target.value)}
                          placeholder="New York"
                        />
                        {errors.city && <p className="text-red-600 text-sm mt-1 font-medium">{errors.city}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">State *</label>
                        <select
                          className={`form-select ${errors.state ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.state}
                          onChange={(e) => updateCustomerInfo('state', e.target.value)}
                        >
                          <option value="">Select</option>
                          {states.map(state => <option key={state} value={state}>{state}</option>)}
                        </select>
                        {errors.state && <p className="text-red-600 text-sm mt-1 font-medium">{errors.state}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-bold text-gray-800 mb-2">ZIP *</label>
                        <input
                          type="text"
                          className={`form-input ${errors.zipCode ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                          value={customerInfo.zipCode}
                          onChange={(e) => updateCustomerInfo('zipCode', e.target.value)}
                          placeholder="10001"
                        />
                        {errors.zipCode && <p className="text-red-600 text-sm mt-1 font-medium">{errors.zipCode}</p>}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Business Details Card */}
                <div className="bg-white border-2 border-gray-200 rounded-xl p-8 shadow-sm">
                  <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-red-600" />
                    Business Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-2">Business Type *</label>
                      <select
                        className={`form-select ${errors.businessType ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                        value={customerInfo.businessType}
                        onChange={(e) => updateCustomerInfo('businessType', e.target.value)}
                      >
                        <option value="">Select Type</option>
                        {businessTypes.map(type => <option key={type} value={type}>{type}</option>)}
                      </select>
                      {errors.businessType && <p className="text-red-600 text-sm mt-1 font-medium">{errors.businessType}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-800 mb-2">Number of Employees *</label>
                      <select
                        className={`form-select ${errors.employeeCount ? 'border-red-500 ring-2 ring-red-200' : 'border-gray-300 focus:border-red-500'}`}
                        value={customerInfo.employeeCount}
                        onChange={(e) => updateCustomerInfo('employeeCount', e.target.value)}
                      >
                        <option value="">Select Range</option>
                        {employeeCounts.map(count => <option key={count} value={count}>{count} employees</option>)}
                      </select>
                      {errors.employeeCount && <p className="text-red-600 text-sm mt-1 font-medium">{errors.employeeCount}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="fade-in-up">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">
                  Review Your Subscription
                </h2>
                <p className="text-gray-600">
                  Everything looks perfect? Let's proceed to finalize your subscription
                </p>
              </div>

              <div 
                className="max-w-5xl mx-auto"
                style={{
                  border: '3px solid #d1d5db',
                  backgroundColor: '#f9fafb',
                  borderRadius: '16px',
                  padding: '3rem'
                }}
              >
                <div className="grid md:grid-cols-2 gap-8">
                  {/* Selected Plan */}
                  <div className="bg-gradient-to-br from-red-600 via-red-600 to-red-700 rounded-2xl shadow-xl p-8 text-white">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                        <Clock className="w-6 h-6" />
                      </div>
                      <h3 className="text-2xl font-bold">Your Plan</h3>
                    </div>
                    {(() => {
                      const selectedPlanData = plans.find(plan => plan.id === selectedPlan);
                      return selectedPlanData ? (
                        <>
                          <div className="mb-6">
                            <h4 className="text-3xl font-bold mb-3">{selectedPlanData.name}</h4>
                            <div className="flex items-baseline gap-2 mb-3">
                              <span className="text-5xl font-bold">${selectedPlanData.price}</span>
                              <span className="text-lg text-red-100">/month</span>
                            </div>
                            <p className="text-red-50">{selectedPlanData.description}</p>
                          </div>
                          <div className="space-y-2">
                            <h5 className="font-bold mb-3 text-red-50">What's Included:</h5>
                            {selectedPlanData.features.map((feature, index) => (
                              <div key={index} className="flex items-start gap-2 bg-white/10 backdrop-blur-sm rounded-lg p-2">
                                <Check className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                <span className="text-sm leading-relaxed">{feature}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      ) : null;
                    })()}
                  </div>

                  {/* Company Info */}
                  <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-gray-200">
                    <h3 className="text-2xl font-bold text-gray-900 mb-6">Company Details</h3>
                    <div className="space-y-4">
                      <div className="pb-4 border-b-2 border-gray-100">
                        <p className="text-sm text-gray-500 mb-1">Company</p>
                        <p className="font-bold text-gray-900">{customerInfo.companyName}</p>
                      </div>
                      <div className="pb-4 border-b-2 border-gray-100">
                        <p className="text-sm text-gray-500 mb-1">Contact</p>
                        <p className="font-bold text-gray-900">{customerInfo.contactName}</p>
                      </div>
                      <div className="pb-4 border-b-2 border-gray-100">
                        <p className="text-sm text-gray-500 mb-1">Email</p>
                        <p className="font-semibold text-gray-900">{customerInfo.email}</p>
                      </div>
                      <div className="pb-4 border-b-2 border-gray-100">
                        <p className="text-sm text-gray-500 mb-1">Phone</p>
                        <p className="font-semibold text-gray-900">{customerInfo.phone}</p>
                      </div>
                      <div className="pb-4 border-b-2 border-gray-100">
                        <p className="text-sm text-gray-500 mb-1">Address</p>
                        <p className="font-semibold text-gray-900">
                          {customerInfo.address}<br />
                          {customerInfo.city}, {customerInfo.state} {customerInfo.zipCode}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Business Type</p>
                          <p className="font-semibold text-gray-900 text-sm">{customerInfo.businessType}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Employees</p>
                          <p className="font-semibold text-gray-900 text-sm">{customerInfo.employeeCount}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                      <Sparkles className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h4 className="font-bold text-blue-900 mb-2">What Happens Next?</h4>
                      <ul className="text-blue-800 space-y-1.5 text-sm">
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          Review and sign the service agreement
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          Set up your secure payment method
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          Schedule your personalized onboarding session
                        </li>
                        <li className="flex items-center gap-2">
                          <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                          Get instant access to 24/7 IT support
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          </div>
        </div>

        {/* Navigation Buttons - Outside Card */}
        <div className="mt-8 flex justify-between items-center w-full">
          {step > 1 ? (
            <button 
              onClick={() => setStep(step - 1)}
              className="btn btn-secondary btn-lg"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </button>
          ) : (
            <div></div>
          )}

          {step < 3 ? (
            <button 
              onClick={handleNext}
              className="btn btn-primary btn-lg"
            >
              Continue
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          ) : (
            <button 
              onClick={handleProceedToContract}
              className="btn btn-primary btn-lg"
            >
              Proceed to Contract
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Subscription;
